<template>
  <v-container fluid>
    <div v-if="!visibleTabs.length" class="pa-6">
      <p class="mb-0" style="color: #a09c94">
        You do not have permission to view product management for this event.
      </p>
    </div>

    <template v-else>
      <v-tabs v-model="currentTab" dark class="pl-3 pr-3">
        <v-tabs-slider></v-tabs-slider>
        <v-tab
          v-for="tabConfig in visibleTabs"
          :key="tabConfig.name"
          :href="`#${tabConfig.name}`"
        >
          {{ tabConfig.label }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="currentTab" dark>
        <v-tab-item
          v-for="tabConfig in visibleTabs"
          :key="tabConfig.name"
          :value="tabConfig.name"
        >
          <v-card flat style="background-color: #272b30">
            <component
              :is="tabConfig.component"
              :products-data="productsData"
              :loading="loading"
              @refresh-data="loadProductManagementData"
            />
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>
  </v-container>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import {
  hasEventOrStallholderReadOrWrite,
  hasReadOrWrite,
} from "@/utils/permissions";
import Products from "../ProductManagement/Products/products.vue";
import ProductsOption from "../ProductManagement/ProductOptions/productOptions.vue";
import Categories from "../ProductManagement/Categories/categories.vue";
import EventTags from "../ProductManagement/Tags/eventTags.vue";
import Layouts from "../ProductManagement/Layouts/Layouts.vue";

const RESTRICTED_TABS = new Set([
  "Tags",
  "Products",
  "ProductsOptions",
  "Categories",
  "Layouts",
]);

export default {
  components: {
    Products,
    ProductsOption,
    Categories,
    EventTags,
    Layouts,
  },
  data() {
    return {
      allTabsConfig: [
        { name: "Categories", label: "Categories", component: "Categories" },
        { name: "Tags", label: "Tags", component: "EventTags" },
        { name: "Products", label: "Products", component: "Products" },
        {
          name: "ProductsOptions",
          label: "Products Options",
          component: "ProductsOption",
        },
        { name: "Layouts", label: "Layouts", component: "Layouts" },
      ],
      productsData: {
        products: [],
        productCategories: [],
        stallholderTags: [],
        eventTags: [],
        productOptions: [],
        layouts: [],
      },
      loading: false,
    };
  },
  computed: {
    ...mapState("admin", [
      "currentProductManagementTab",
      "currentStallholder",
      "currentStallHolders",
    ]),
    ...mapState("filter", ["globalEventFilter", "IsAdmin"]),

    readableStallholders() {
      return (this.currentStallHolders || []).filter((sh) =>
        hasReadOrWrite(sh.Permission)
      );
    },

    canViewRestrictedTabs() {
      return (
        this.IsAdmin ||
        hasEventOrStallholderReadOrWrite(
          this.globalEventFilter?.Permission,
          this.currentStallholder?.Permission
        ) ||
        this.readableStallholders.length > 0
      );
    },

    visibleTabs() {
      if (this.canViewRestrictedTabs) {
        return this.allTabsConfig;
      }
      return this.allTabsConfig.filter(
        (tab) => !RESTRICTED_TABS.has(tab.name)
      );
    },

    currentTab: {
      get() {
        const tab = this.currentProductManagementTab;
        if (this.visibleTabs.some((t) => t.name === tab)) {
          return tab;
        }
        return this.visibleTabs[0]?.name || "Categories";
      },
      set(value) {
        this.setCurrentProductManagementTab(value);
      },
    },
  },
  watch: {
    "globalEventFilter.EventId"(newVal) {
      if (newVal) {
        this.loadProductManagementData();
      }
    },
    currentStallholder: {
      handler(newStallholder) {
        this.ensureReadableStallholder();
        if (newStallholder) {
          this.loadProductManagementData();
        }
      },
      immediate: true,
    },
    currentStallHolders() {
      this.ensureReadableStallholder();
    },
    visibleTabs: {
      handler(tabs) {
        if (
          !tabs.some((t) => t.name === this.currentProductManagementTab)
        ) {
          this.setCurrentProductManagementTab(tabs[0]?.name || "Categories");
        }
      },
      immediate: true,
    },
    canViewRestrictedTabs() {
      this.loadProductManagementData();
    },
  },
  methods: {
    ...mapMutations("admin", [
      "setCurrentProductManagementTab",
      "setCurrentStallholder",
    ]),
    ...mapActions("admin", ["getProducts", "getLayouts"]),

    ensureReadableStallholder() {
      if (
        this.IsAdmin ||
        hasReadOrWrite(this.globalEventFilter?.Permission) ||
        hasReadOrWrite(this.currentStallholder?.Permission)
      ) {
        return;
      }
      const next = this.readableStallholders[0];
      if (next && next.StallholderId !== this.currentStallholder?.StallholderId) {
        this.setCurrentStallholder(next);
      }
    },

    async loadProductManagementData() {
      if (
        !this.globalEventFilter.EventId ||
        !this.currentStallholder?.StallholderId
      ) {
        this.resetProductsData();
        return;
      }

      if (!this.canViewRestrictedTabs) {
        this.resetProductsData();
        return;
      }

      this.loading = true;

      try {
        const paramObj = {
          EventId: this.globalEventFilter.EventId,
          StallholderId: this.currentStallholder.StallholderId,
        };

        const productsResponse = await this.getProducts(paramObj);

        const paramLayoutObj = {
          eventId: this.globalEventFilter.EventId,
          stallholderId: this.currentStallholder.StallholderId,
        };

        let layouts = [];
        try {
          const layoutsResponse = await this.getLayouts(paramLayoutObj);
          layouts = layoutsResponse?.Layouts || [];
        } catch (layoutErr) {
          console.error("Error loading layouts:", layoutErr);
        }

        this.productsData = {
          products: productsResponse.Products || [],
          productCategories: productsResponse.ProductCategories || [],
          stallholderTags: productsResponse.StallholderTags || [],
          eventTags: productsResponse.EventTags || [],
          productOptions: productsResponse.ProductOptions || [],
          layouts,
        };
      } catch (error) {
        console.error("Error loading product management data:", error);
        this.resetProductsData();
      } finally {
        this.loading = false;
      }
    },

    resetProductsData() {
      this.productsData = {
        products: [],
        productCategories: [],
        stallholderTags: [],
        eventTags: [],
        productOptions: [],
        layouts: [],
      };
    },
  },
};
</script>

<style scoped>
body {
  background-color: #272b30;
}
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
p {
  color: #6e6e6e;
  font-family: sans-serif;
}
</style>
